import React, { useState } from 'react';
import WindowBody from '../components/Window/WindowBody';
import WindowFooter from '../components/Window/WindowFooter';
import { UiButton } from '../components/UiButton';
import { CadeadoTravada, CORES_SITUACAO, SituacaoGlifo } from '../components/SituacaoGlifo';
import { useWindowManager } from '../components/Window';
import { resolveWindowSize } from '../components/Window/windowTypes';
import { resolveRoute } from '../components/Core/ComponentRegistry';
import { useNotification } from '../components/Core/NotificationProvider';
import { get, post } from '../services/ApiService';
import { Util } from '../util/Util';
import './ObraForm.scss';

interface Tarefa {
    id: number;
    numero?: number;
    tipo?: string;
    situacao?: string;
    situacaoNome?: string;
    cor?: string;
    responsavel?: string | null;
    nota?: string | null;
    idade?: number | null;
    prazoDias?: number | null;
    atrasada?: boolean;
    travada?: boolean;
    motivoTravada?: string | null;
}

interface Trilha {
    perfil: string;
    nome: string;
    tarefas: Tarefa[];
}

// Aberta é o que ainda vai andar. `reprovada` não conta como aberta: a reprovação
// devolve a tarefa original para `andamento`, e é ela que aparece na contagem.
const SITUACOES_ABERTAS = ['aguarda', 'liberada', 'andamento'];
const SITUACOES_TERMINAIS = ['concluida', 'cancelada'];

function plural(quantidade: number, singular: string, plural: string) {
    return `${quantidade} ${quantidade === 1 ? singular : plural}`;
}

export default function ObraForm(props: any) {
    const obra = props.data;
    const windowManager = useWindowManager();
    const notification = useNotification();
    const [selecionada, setSelecionada] = useState<number | null>(null);

    // Abre o form da tarefa pelo mesmo caminho do grid: rota resolvida no registry,
    // carga por `action=edit` e gravação por `action=edit`.
    const abrirTarefa = async () => {
        if (!selecionada) return;

        const rota = resolveRoute('tar_tartarefa_edit');
        if (!rota) return;

        const carregando = notification.showLoading('Carregando...');
        let dadosTarefa: any;
        try {
            const linhas = encodeURIComponent(JSON.stringify([{ id: selecionada }]));
            const resposta = await get(`/?route=tartarefa/form&action=edit&rows=${linhas}`);
            notification.hideLoading(carregando);
            if (resposta.status === 200 && resposta.data?.success) {
                dadosTarefa = resposta.data.payload;
            } else {
                notification.error(resposta.data?.message || 'Erro ao carregar a tarefa');
                return;
            }
        } catch (erro: any) {
            notification.hideLoading(carregando);
            notification.error(erro.message || 'Erro ao carregar a tarefa');
            return;
        }

        let janelaId: string;

        const ConteudoDaJanela = () => {
            const salvar = async (dadosDoForm: any) => {
                const salvando = notification.showLoading('Salvando...');
                try {
                    const resposta = await post('/?route=tartarefa/form&action=edit', { dados: dadosDoForm });
                    notification.hideLoading(salvando);
                    if (resposta.status === 200 && resposta.data?.success) {
                        windowManager.closeWindow(janelaId);
                        notification.success(resposta.data?.message || 'Tarefa salva');
                    } else {
                        notification.error(resposta.data?.message || 'Erro ao salvar');
                    }
                } catch (erro: any) {
                    notification.hideLoading(salvando);
                    notification.error(erro.message || 'Erro ao salvar');
                }
            };

            return (
                <rota.component
                    data={dadosTarefa}
                    loading={false}
                    triggerSubmit={salvar}
                    triggerSubmitAsNew={salvar}
                    triggerClose={() => windowManager.closeWindow(janelaId)}
                />
            );
        };

        janelaId = windowManager.openWindow({
            title: rota.title || 'Tarefa',
            component: ConteudoDaJanela,
            size: resolveWindowSize(rota.windowSize ?? 'lg'),
            loading: false,
            data: null,
        });
    };

    if (!obra) {
        return (
            <>
                <WindowBody><div className="obra-vazio">Obra não encontrada</div></WindowBody>
                <WindowFooter>
                    <UiButton variant="secondary" onClick={props.triggerClose}>Fechar</UiButton>
                </WindowFooter>
            </>
        );
    }

    const trilhas: Trilha[] = obra.trilhas || [];
    const resumo = obra.resumo || {};
    const financeiro = obra.financeiro || {};

    return (
        <>
            <WindowBody>
                <div className="obra-painel">
                    <div className="obra-cabecalho">
                        <h4>Obra {obra.identificador} · {obra.cliente}</h4>
                        <div className="obra-subtitulo">
                            {obra.cidade}
                            {obra.kwp ? ` · ${obra.kwp} kWp` : ''}
                            {` · ${plural(resumo.abertas || 0, 'tarefa aberta', 'tarefas abertas')}`}
                            {resumo.travadas ? <span className="obra-alerta"> · {plural(resumo.travadas, 'travada', 'travadas')}</span> : null}
                            {resumo.reprovadas ? <span className="obra-alerta"> · {plural(resumo.reprovadas, 'reprovação', 'reprovações')}</span> : null}
                        </div>
                    </div>

                    {trilhas.map((trilha) => {
                        const abertas = trilha.tarefas.filter((t) => SITUACOES_ABERTAS.includes(String(t.situacao))).length;

                        return (
                            <fieldset key={trilha.perfil}>
                                <legend>
                                    {trilha.nome}
                                    <span className="obra-trilha-contagem"> {abertas} aberta{abertas === 1 ? '' : 's'}</span>
                                </legend>

                                {trilha.tarefas.map((tarefa) => {
                                    const terminal = SITUACOES_TERMINAIS.includes(String(tarefa.situacao));
                                    const classes = [
                                        'obra-tarefa',
                                        terminal ? 'is-terminal' : '',
                                        tarefa.atrasada ? 'is-atrasada' : '',
                                        selecionada === tarefa.id ? 'is-selecionada' : '',
                                    ].filter(Boolean).join(' ');

                                    // O impedimento vence a situação no desenho: é o que precisa ser
                                    // lido primeiro, como no grid.
                                    const situacao = tarefa.travada ? 'travada' : String(tarefa.situacao || '');
                                    const cor = tarefa.travada
                                        ? CORES_SITUACAO.travada
                                        : (tarefa.cor || CORES_SITUACAO[situacao] || '#adb5bd');

                                    return (
                                        <div
                                            key={tarefa.id}
                                            className={classes}
                                            role="button"
                                            tabIndex={0}
                                            aria-pressed={selecionada === tarefa.id}
                                            onClick={() => setSelecionada(tarefa.id)}
                                            onDoubleClick={abrirTarefa}
                                            onKeyDown={(evento) => {
                                                if (evento.key !== 'Enter' && evento.key !== ' ') return;
                                                evento.preventDefault();
                                                setSelecionada(tarefa.id);
                                            }}
                                        >
                                            <div className="obra-tarefa-tipo">
                                                <SituacaoGlifo
                                                    situacao={situacao}
                                                    cor={cor}
                                                    size={14}
                                                    title={tarefa.situacaoNome || tarefa.situacao}
                                                />
                                                <span className="obra-tarefa-nome">{tarefa.tipo}</span>
                                                {tarefa.travada ? <CadeadoTravada/> : null}
                                            </div>

                                            <div className="obra-tarefa-responsavel">
                                                {tarefa.responsavel
                                                    ? tarefa.responsavel
                                                    : <span className="obra-tarefa-sem-responsavel">não atribuída</span>}
                                            </div>

                                            <div className={`obra-tarefa-nota${tarefa.travada ? ' is-travada' : ''}`}>
                                                {tarefa.travada ? tarefa.motivoTravada : tarefa.nota}
                                            </div>

                                            <div className="obra-tarefa-idade">
                                                {tarefa.idade === null || tarefa.idade === undefined ? '' : `há ${tarefa.idade} d`}
                                            </div>
                                        </div>
                                    );
                                })}

                                {trilha.tarefas.length === 0 ? <div className="obra-trilha-vazia">Sem tarefas</div> : null}
                            </fieldset>
                        );
                    })}

                    <fieldset>
                        <legend>Financeiro</legend>
                        <div className="obra-financeiro">
                            <div className="obra-financeiro-item">
                                <div className="obra-financeiro-label">A receber em aberto</div>
                                <div className="obra-financeiro-valor">{Util.moeda(Number(financeiro.aReceberAberto || 0))}</div>
                            </div>
                            <div className="obra-financeiro-item">
                                <div className="obra-financeiro-label">A pagar em aberto</div>
                                <div className="obra-financeiro-valor">{Util.moeda(Number(financeiro.aPagarAberto || 0))}</div>
                            </div>
                        </div>
                        <div className="obra-rodape">
                            Do módulo financeiro: o financeiro não é tarefa, é lançamento.
                        </div>
                    </fieldset>
                </div>
            </WindowBody>

            <WindowFooter>
                <UiButton variant="secondary" onClick={props.triggerClose}>Fechar</UiButton>
                <UiButton variant="primary" onClick={abrirTarefa} disabled={!selecionada}>Abrir tarefa</UiButton>
            </WindowFooter>
        </>
    );
}
